import { Button, DetailPanelNavigation, Modal, ModalClose, ModalContent, ModalTitle } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, X } from "lucide-react";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { api, type EvidenceAnswer, type EvidenceCitation, type EvidenceFilters } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Markdown, plain } from "~/core/ui/markdown";
import { Async, Empty } from "~/core/ui/states";
import type { FilterState } from "~/modules/analytics/components/filters";
import { magnitudeColor } from "~/modules/analytics/lib/bar-color";

/**
 * The way down from a number to the rows it was counted from.
 *
 * Every figure in this panel is an aggregate over answers we paid for, and
 * until this existed the only thing a customer could do with one was believe
 * it. A share of 34% and a cell reading 3/5 are claims; the three answers that
 * cited you, with what they said and what they linked, are evidence. This
 * component is how the second gets under the first.
 *
 * It is a drawer and not a page for one reason: a drill-down is a question
 * about the row you are looking at, and answering it by navigating away takes
 * the row with it. It opens over the content column rather than beside it —
 * the assistant already owns the right-hand track, and a detail panel that
 * fought it for width would make the customer choose between the evidence and
 * the conversation about it.
 *
 * It holds a *stack*. A citation list leads to an answer, an answer leads to
 * the rival it named, and each of those is a step deeper into the same
 * question — so back is back, and closing returns the screen underneath
 * untouched.
 */

// ---------------------------------------------------------------------------
// What can be opened
// ---------------------------------------------------------------------------

export type DetailView =
	/** Questions, counted the way the overview's headline counts them. */
	| { kind: "questions"; label: string; filters?: EvidenceFilters }
	/** Answers: one row per replication. The grain of a matrix cell. */
	| { kind: "answers"; label: string; filters?: EvidenceFilters }
	/** Citations: one row per link. The grain of "cited 31 times". */
	| { kind: "citations"; label: string; filters?: EvidenceFilters }
	/** One answer, in full. */
	| { kind: "answer"; label: string; sampleId: string }
	/** A rival, opened from their name. */
	| { kind: "rival"; label: string; domain: string }
	/** One crawler, or one path. */
	| { kind: "crawler"; label: string; bot?: string; host?: string; path?: string };

interface Detail {
	/** Open a fresh stack. What a click on the screen behind does. */
	open: (view: DetailView, siblings?: DetailView[]) => void;
	/** Go one level deeper, keeping the way back. What a click inside does. */
	push: (view: DetailView, siblings?: DetailView[]) => void;
}

interface DetailFrame {
	navigable: boolean;
	view: DetailView;
	siblings: DetailView[];
	index: number;
}

function frame(view: DetailView, siblings?: DetailView[]): DetailFrame {
	return {
		view,
		siblings: siblings ?? [view],
		index: siblings ? siblings.indexOf(view) : 0,
		navigable: siblings !== undefined,
	};
}

const Context = createContext<Detail | null>(null);

const KINDS = new Set(["questions", "answers", "citations", "answer", "rival", "crawler"]);

/**
 * The stack as a link carries it: the views, without the sibling lists a
 * table handed in, which are the table's and not the link's. Anything that is
 * not a list of known views is ignored — a hand-edited link opens nothing
 * rather than a drawer that fails.
 */
export function readStack(param: string | null): DetailFrame[] {
	if (!param) return [];
	try {
		const views: unknown = JSON.parse(param);
		if (!Array.isArray(views)) return [];
		return views
			.filter(
				(v): v is DetailView => typeof v === "object" && v !== null && KINDS.has(v.kind) && typeof v.label === "string",
			)
			.map((view) => frame(view));
	} catch {
		return [];
	}
}

/**
 * Available on every screen inside a brand.
 *
 * Returns a no-op outside the provider rather than throwing: the drill-downs
 * are an affordance on a number, and a screen rendered outside a brand — the
 * wizard, a claim — should not crash for having a number on it.
 */
export function useDetail(): Detail {
	return (
		useContext(Context) ?? {
			open: () => {},
			push: () => {},
		}
	);
}

export function DetailProvider({
	entityId,
	filters,
	children,
}: {
	entityId: string;
	/** The screen's own range, engine and locale. A drill-down inherits them,
	 *  because a drawer opened from a number filtered to 7 days that showed 90
	 *  would not be the rows behind that number. */
	filters: FilterState;
	children: ReactNode;
}) {
	const [params, setParams] = useSearchParams();
	// A drill-down used to live only here, so the answer somebody found could
	// not be sent to anybody: the link opened the screen with the drawer shut.
	const [stack, setStack] = useState<DetailFrame[]>(() => readStack(params.get("detail")));
	useEffect(() => {
		setParams(
			(current) => {
				const next = new URLSearchParams(current);
				const views = stack.map((f) => f.view);
				if (views.length > 0) next.set("detail", JSON.stringify(views));
				else next.delete("detail");
				return next;
			},
			{ replace: true },
		);
	}, [stack, setParams]);

	const value = useMemo<Detail>(
		() => ({
			open: (view, siblings) => setStack([frame(view, siblings)]),
			push: (view, siblings) => setStack((current) => [...current, frame(view, siblings)]),
		}),
		[],
	);

	const back = useCallback(() => setStack((current) => current.slice(0, -1)), []);
	const close = useCallback(() => setStack([]), []);
	const navigate = useCallback(
		(delta: number) =>
			setStack((current) => {
				const active = current.at(-1);
				if (!active || active.index < 0) return current;
				const index = active.index + delta;
				const view = active.siblings[index];
				return view ? [...current.slice(0, -1), { ...active, view, index }] : current;
			}),
		[],
	);

	return (
		<Context.Provider value={value}>
			{children}
			<Drawer entityId={entityId} filters={filters} stack={stack} onBack={back} onClose={close} onNavigate={navigate} />
		</Context.Provider>
	);
}

// ---------------------------------------------------------------------------
// The drawer
// ---------------------------------------------------------------------------

function Drawer({
	entityId,
	filters,
	stack,
	onBack,
	onClose,
	onNavigate,
}: {
	entityId: string;
	filters: FilterState;
	stack: DetailFrame[];
	onBack: () => void;
	onClose: () => void;
	onNavigate: (delta: number) => void;
}) {
	const t = useT();
	const active = stack.at(-1);
	const view = active?.view;

	return (
		<Modal open={stack.length > 0} onOpenChange={(open) => (open ? null : onClose())}>
			<ModalContent side="right" aria-describedby={undefined}>
				<div className="detail-panel-head">
					{stack.length > 1 ? (
						<Button variant="ghost" size="icon" type="button" onClick={onBack} aria-label={t("detail.back")}>
							<ArrowLeft aria-hidden />
						</Button>
					) : null}
					{/* The title is the row that was clicked, verbatim where it can
						    be: a drawer headed "Detail" makes the reader check which
						    number they opened. */}
					<ModalTitle className="detail-panel-title">{view?.label ?? ""}</ModalTitle>
					{active?.navigable ? (
						<DetailPanelNavigation
							previousLabel={t("detail.previous")}
							nextLabel={t("detail.next")}
							onPrevious={active.index > 0 ? () => onNavigate(-1) : undefined}
							onNext={active.index >= 0 && active.index < active.siblings.length - 1 ? () => onNavigate(1) : undefined}
						/>
					) : null}
					<ModalClose asChild>
						<Button variant="ghost" size="icon" aria-label={t("detail.close")}>
							<X aria-hidden />
						</Button>
					</ModalClose>
				</div>

				<div key={`${stack.length}:${active?.index}`} className="detail-panel-body">
					{view ? <View entityId={entityId} filters={filters} view={view} /> : null}
				</div>
			</ModalContent>
		</Modal>
	);
}

function View({ entityId, filters, view }: { entityId: string; filters: FilterState; view: DetailView }) {
	// The screen's filters, with the view's own on top. A cell on the matrix
	// names its engine even when the filter bar says "all engines", and the
	// number it opened from was counted that way.
	const scope: EvidenceFilters = { ...filters, ...(("filters" in view && view.filters) || {}) };

	switch (view.kind) {
		case "questions":
			return <Questions entityId={entityId} filters={{ ...scope, group: "question" }} />;
		case "answers":
			return <Answers entityId={entityId} filters={{ ...scope, group: "answer" }} />;
		case "citations":
			return <Citations entityId={entityId} filters={{ ...scope, group: "citation" }} />;
		case "answer":
			return <OneAnswer entityId={entityId} sampleId={view.sampleId} />;
		case "rival":
			return <Rival entityId={entityId} domain={view.domain} filters={filters} />;
		case "crawler":
			return <Crawler entityId={entityId} view={view} filters={filters} />;
	}
}

// ---------------------------------------------------------------------------
// Questions
// ---------------------------------------------------------------------------

function Questions({ entityId, filters }: { entityId: string; filters: EvidenceFilters }) {
	const t = useT();
	const { push } = useDetail();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "evidence", filters],
		queryFn: () => api.evidence(entityId, filters),
		...read,
	});

	return (
		<Async state={state} empty={<Empty title={t("detail.empty")} />}>
			{(data) => {
				const rows = data.questions ?? [];
				if (rows.length === 0) return <Empty title={t("detail.empty")} />;
				return (
					<>
						<Count shown={rows.length} total={data.total} />
						<ul className="detail-list">
							{rows.map((row) => (
								<li key={row.question_id}>
									<Button
										variant="ghost"
										className="detail-row h-auto min-h-(--control-h) w-full items-start justify-start whitespace-normal px-0 py-(--row-y) text-left"
										type="button"
										// `cited` is dropped on the way down. It selected *questions*
										// the brand wins; carried onto the answers it would hide the
										// replications that did not cite you — which are half of
										// what somebody opens a won question to see.
										onClick={() =>
											push({
												kind: "answers",
												label: row.question,
												filters: { ...filters, group: undefined, cited: undefined, question: row.question_id },
											})
										}
									>
										<span className="detail-row-main">{row.question}</span>
										<span className="detail-row-meta">
											<span className={row.majority ? "pill pill-success" : "pill"}>
												{t(row.majority ? "detail.cited" : "detail.notCited")}
											</span>
											{/* Per engine, because the majority is decided one
											    engine at a time and a single fraction would hide
											    that three of four engines never name you. */}
											{row.engines.map((cell) => (
												<span className="mono faint" key={cell.engine}>
													{cell.engine} {cell.cited}/{cell.replications}
												</span>
											))}
										</span>
									</Button>
								</li>
							))}
						</ul>
					</>
				);
			}}
		</Async>
	);
}

// ---------------------------------------------------------------------------
// Answers
// ---------------------------------------------------------------------------

function Answers({ entityId, filters }: { entityId: string; filters: EvidenceFilters }) {
	const t = useT();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "evidence", filters],
		queryFn: () => api.evidence(entityId, filters),
		...read,
	});

	return (
		<Async state={state} empty={<Empty title={t("detail.empty")} />}>
			{(data) => {
				const rows = data.answers ?? [];
				if (rows.length === 0) return <Empty title={t("detail.empty")} />;
				return (
					<>
						<Count shown={rows.length} total={data.total} />
						<div className="detail-cards">
							{rows.map((row) => (
								<AnswerCard key={row.id} answer={row} />
							))}
						</div>
					</>
				);
			}}
		</Async>
	);
}

/**
 * One answer, as much of it as belongs in a list.
 *
 * The opening of the text and everything it linked. Whether the brand was cited
 * is a state, so it is the one coloured thing on the card — and it is a word
 * rather than a tint alone, because it is the answer to the question the reader
 * opened the drawer with.
 */
function AnswerCard({ answer }: { answer: EvidenceAnswer }) {
	const t = useT();
	const { locale } = useI18n();
	const { push } = useDetail();

	return (
		<article className="detail-card">
			<div className="detail-card-head">
				<span className={answer.cited ? "pill pill-success" : "pill"}>
					{t(answer.cited ? "detail.cited" : "detail.notCited")}
				</span>
				<span className="faint">
					{answer.engine} · {fmt.relative(locale, answer.created_at)}
				</span>
				{answer.position !== null ? <span className="pill">{t("detail.namedAt", { n: answer.position })}</span> : null}
				{answer.sentiment ? <span className="pill">{t(`perception.mood.${answer.sentiment}`)}</span> : null}
			</div>

			<p className="detail-quote">{plain(answer.excerpt) || t("detail.noText")}</p>
			{answer.descriptor ? <p className="faint">{answer.descriptor}</p> : null}

			<Cites items={answer.citations} />

			<Button
				variant="default"
				type="button"
				onClick={() => push({ kind: "answer", label: answer.question, sampleId: answer.id })}
			>
				{t("detail.fullAnswer")}
			</Button>
		</article>
	);
}

/** What an answer linked to, and whose it is. */
function Cites({ items }: { items: EvidenceCitation[] }) {
	const t = useT();
	const { push } = useDetail();
	if (items.length === 0) return <p className="faint">{t("detail.noSources")}</p>;

	return (
		<ul className="detail-cites">
			{items.map((cite) => (
				<li key={`${cite.url}${cite.position}`}>
					{/* The domain opens the rival; the title opens the page. Two
					    different questions — "who is this" and "what did they say" —
					    and one link for both answered neither well. */}
					<Button
						variant="link"
						className="link-button h-auto justify-start whitespace-normal p-0 text-left"
						type="button"
						onClick={() => push({ kind: "rival", label: cite.domain, domain: cite.domain })}
					>
						{cite.domain}
					</Button>
					<a href={cite.url} target="_blank" rel="noopener noreferrer nofollow" className="detail-cite-title">
						{cite.title || cite.url}
						<ExternalLink aria-hidden />
					</a>
					{cite.ours ? <span className="pill pill-success">{t("citations.kind.yours")}</span> : null}
					{cite.is_ondit ? <span className="pill pill-accent">{t("citations.kind.ondit")}</span> : null}
				</li>
			))}
		</ul>
	);
}

// ---------------------------------------------------------------------------
// Citations
// ---------------------------------------------------------------------------

function Citations({ entityId, filters }: { entityId: string; filters: EvidenceFilters }) {
	const t = useT();
	const { locale } = useI18n();
	const { push } = useDetail();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "evidence", filters],
		queryFn: () => api.evidence(entityId, filters),
		...read,
	});

	return (
		<Async state={state} empty={<Empty title={t("detail.empty")} />}>
			{(data) => {
				const rows = data.citations ?? [];
				if (rows.length === 0) return <Empty title={t("detail.empty")} />;
				return (
					<>
						<Count shown={rows.length} total={data.total} />
						<ul className="detail-list">
							{rows.map((row) => (
								<li key={row.id}>
									<div className="detail-row is-static">
										<span className="detail-row-main">
											<a href={row.url} target="_blank" rel="noopener noreferrer nofollow">
												{row.title || row.url}
												<ExternalLink aria-hidden />
											</a>
											<span className="faint">{row.domain}</span>
										</span>
										<span className="detail-row-meta">
											<Button
												variant="link"
												className="link-button h-auto justify-start whitespace-normal p-0 text-left"
												type="button"
												onClick={() => push({ kind: "answer", label: row.question, sampleId: row.sample_id })}
											>
												{row.question}
											</Button>
											<span className="faint">
												{row.engine} · {fmt.relative(locale, row.created_at)}
											</span>
										</span>
									</div>
								</li>
							))}
						</ul>
					</>
				);
			}}
		</Async>
	);
}

// ---------------------------------------------------------------------------
// One answer, in full
// ---------------------------------------------------------------------------

/**
 * What the engine actually said.
 *
 * The bottom of every drill-down in this panel, and the only screen where a
 * customer reads the raw material their numbers are made of. A gateway refusal
 * is shown as one — it is kept as evidence and never counted as an answer, and
 * a reader who found it in a list would otherwise take it for a bad answer
 * rather than for no answer at all.
 */
function OneAnswer({ entityId, sampleId }: { entityId: string; sampleId: string }) {
	const t = useT();
	const { locale } = useI18n();
	const { push } = useDetail();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "answer", sampleId],
		queryFn: () => api.answer(entityId, sampleId),
		...read,
	});

	return (
		<Async state={state}>
			{(data) => (
				<>
					<p className="detail-lede">{data.question}</p>
					<p className="faint">
						{data.engine} · {data.model} · {data.week} · {fmt.date(locale, data.created_at)}
					</p>

					{data.audience?.persona_name && (
						<details className="detail-section">
							<summary>
								{t("personas.axis")} · {data.audience.persona_name}
							</summary>
							<dl>
								{Object.entries(data.audience.profile ?? {})
									.filter(([, v]) => v)
									.map(([k, v]) => (
										<div key={k}>
											<dt>{t(`audience.field.${k}`)}</dt>
											<dd>{v}</dd>
										</div>
									))}
							</dl>
						</details>
					)}

					{data.notice ? (
						<div className="banner banner-warn">
							{t(
								data.notice === "response_truncated"
									? "detail.truncated"
									: data.notice === "empty_response"
										? "detail.emptyResponse"
										: "detail.notice",
							)}
						</div>
					) : null}

					{data.text ? (
						<div className="detail-answer">
							<Markdown text={data.text} marks={data.mentions.map((m) => ({ text: m.name, you: m.you }))} />
						</div>
					) : (
						<p className="detail-answer">{t("detail.noText")}</p>
					)}

					{data.web_queries.length > 0 ? (
						<section className="detail-section">
							<h3>{t("detail.webQueries")}</h3>
							<div className="row-actions">
								{data.web_queries.map((q) => (
									<span className="pill mono" key={q}>
										{q}
									</span>
								))}
							</div>
						</section>
					) : null}

					{data.mentions.length > 0 ? (
						<section className="detail-section">
							<h3>{t("detail.mentions")}</h3>
							<ul className="detail-list">
								{data.mentions.map((mention) => (
									<li key={mention.entity_id}>
										<div className="detail-row is-static">
											<span className="detail-row-main">
												<span className="rank num">{mention.position}</span>
												{mention.name}
												{mention.you ? <span className="pill pill-success">{t("detail.you")}</span> : null}
											</span>
											<span className="detail-row-meta">
												{mention.sentiment ? (
													<span className="pill">{t(`perception.mood.${mention.sentiment}`)}</span>
												) : null}
												{mention.descriptor ? <span className="faint">{mention.descriptor}</span> : null}
											</span>
										</div>
									</li>
								))}
							</ul>
						</section>
					) : null}

					<section className="detail-section">
						<h3>{t("detail.sources")}</h3>
						<Cites items={data.citations} />
					</section>

					<Button
						variant="default"
						type="button"
						onClick={() =>
							push({
								kind: "answers",
								label: data.question,
								filters: { question: data.question_id },
							})
						}
					>
						{t("detail.otherAnswers")}
					</Button>
				</>
			)}
		</Async>
	);
}

// ---------------------------------------------------------------------------
// A rival
// ---------------------------------------------------------------------------

/**
 * Who this domain is, and why they keep turning up.
 *
 * The rival tables in this panel rank domains, which answers "who" and never
 * "why them". The head-to-head list is the useful half: a question where four
 * answers cited them and none cited you is a brief, and it is the same fact the
 * gap list is built from, arrived at from the other direction.
 */
function Rival({ entityId, domain, filters }: { entityId: string; domain: string; filters: FilterState }) {
	const t = useT();
	const { locale } = useI18n();
	const { push } = useDetail();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "rival", domain, { range: filters.range, locale: filters.locale }],
		queryFn: () => api.rival(entityId, domain, filters),
		...read,
	});

	return (
		<Async state={state}>
			{(data) => (
				<>
					<div className="detail-stats">
						<div>
							<span className="label">{t("competitors.share")}</span>
							<span className="value num">{data.share === null ? "—" : fmt.percent(locale, data.share)}</span>
						</div>
						<div>
							<span className="label">{t("competitors.questions")}</span>
							<span className="value num">{data.cited_questions}</span>
						</div>
						<div>
							<span className="label">{t("detail.domain")}</span>
							<span className="value mono">{data.domain}</span>
						</div>
					</div>

					{data.slug ? (
						<Button asChild variant="default">
							<a href={`${SITE}/e/${data.slug}`} target="_blank" rel="noopener noreferrer">
								{t("detail.openListing")}
								<ExternalLink aria-hidden />
							</a>
						</Button>
					) : null}

					{data.trend.length > 1 ? (
						<section className="detail-section">
							<h3>{t("overview.trend")}</h3>
							<Bars
								percentage
								rows={[...data.trend]
									.reverse()
									.map((point) => ({ label: point.week, value: point.share, text: fmt.percent(locale, point.share) }))}
							/>
						</section>
					) : null}

					{data.questions.length > 0 ? (
						<section className="detail-section">
							<h3>{t("detail.headToHead")}</h3>
							<ul className="detail-list">
								{data.questions.map((row) => (
									<li key={row.question_id}>
										<Button
											variant="ghost"
											className="detail-row h-auto min-h-(--control-h) w-full items-start justify-start whitespace-normal px-0 py-(--row-y) text-left"
											type="button"
											onClick={() =>
												push({
													kind: "answers",
													label: row.question,
													filters: { question: row.question_id },
												})
											}
										>
											<span className="detail-row-main">{row.question}</span>
											<span className="detail-row-meta">
												{/* Both counts against the same denominator, so
												    "4 · 0 of 4" reads as a question they own. */}
												<span className="mono">
													{t("detail.theirs")} {row.theirs}
												</span>
												<span className={row.yours > 0 ? "mono" : "mono warn"}>
													{t("detail.yours")} {row.yours}
												</span>
												<span className="faint mono">{t("detail.ofAnswers", { n: row.answers })}</span>
											</span>
										</Button>
									</li>
								))}
							</ul>
						</section>
					) : null}

					{data.pages.length > 0 ? (
						<section className="detail-section">
							<h3>{t("detail.rivalPages")}</h3>
							<ul className="detail-list">
								{data.pages.map((page) => (
									<li key={page.url}>
										<div className="detail-row is-static">
											<span className="detail-row-main">
												<a href={page.url} target="_blank" rel="noopener noreferrer nofollow">
													{page.title || page.url}
													<ExternalLink aria-hidden />
												</a>
											</span>
											<span className="detail-row-meta">
												<Button
													variant="link"
													className="link-button mono h-auto justify-start whitespace-normal p-0 text-left"
													type="button"
													onClick={() =>
														push({
															kind: "citations",
															label: page.url,
															filters: { url: page.url },
														})
													}
												>
													{page.count}
												</Button>
											</span>
										</div>
									</li>
								))}
							</ul>
						</section>
					) : null}
				</>
			)}
		</Async>
	);
}

const SITE = import.meta.env.VITE_SITE_ORIGIN || "http://localhost:9901";

// ---------------------------------------------------------------------------
// A crawler, or a path
// ---------------------------------------------------------------------------

function Crawler({
	entityId,
	view,
	filters,
}: {
	entityId: string;
	view: Extract<DetailView, { kind: "crawler" }>;
	filters: FilterState;
}) {
	const t = useT();
	const { locale } = useI18n();
	const { push } = useDetail();
	const scope = { range: filters.range, bot: view.bot, host: view.host, path: view.path };
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "crawl-detail", scope],
		queryFn: () => api.crawlDetail(entityId, scope),
		...read,
	});

	return (
		<Async state={state}>
			{(data) => (
				<>
					{data.series.length > 0 ? (
						<section className="detail-section">
							<h3>{t("crawlers.daily")}</h3>
							<Bars
								rows={data.series.map((day) => ({
									label: day.date.slice(5),
									value: day.hits,
									text: fmt.number(locale, day.hits),
									warn: day.errors > 0,
								}))}
							/>
						</section>
					) : null}

					<section className="detail-section">
						<h3>{t(view.bot ? "detail.crawlerPaths" : "detail.crawlerBots")}</h3>
						{data.rows.length === 0 ? (
							<Empty title={t("detail.empty")} />
						) : (
							<ul className="detail-list">
								{data.rows.map((row) => (
									<li key={`${row.key}${row.path}`}>
										<div className="detail-row is-static">
											<span className="detail-row-main mono">
												{view.bot ? (
													<Button
														variant="link"
														className="link-button mono h-auto justify-start whitespace-normal p-0 text-left"
														type="button"
														onClick={() => push({ kind: "crawler", label: row.path, path: row.path, host: row.key })}
													>
														{row.path}
													</Button>
												) : (
													<>
														{row.label ?? row.key}
														{row.purpose ? <span className="pill">{t(`crawlers.purpose.${row.purpose}`)}</span> : null}
													</>
												)}
											</span>
											<span className="detail-row-meta">
												<span className="mono">
													{t("crawlers.hits")} {fmt.number(locale, row.hits)}
												</span>
												<span className={row.errors > 0 ? "mono warn" : "mono faint"}>
													{t("crawlers.errors")} {fmt.number(locale, row.errors)}
												</span>
											</span>
										</div>
									</li>
								))}
							</ul>
						)}
					</section>
				</>
			)}
		</Async>
	);
}

// ---------------------------------------------------------------------------
// Shared furniture
// ---------------------------------------------------------------------------

/**
 * How many rows are on screen, against how many there are.
 *
 * The endpoints cap at two hundred. A drawer that quietly showed the first
 * hundred of a thousand would be a different number from the one it opened
 * from, which is the one thing this whole feature exists not to do.
 */
function Count({ shown, total }: { shown: number; total: number }) {
	const t = useT();
	return (
		<p className="detail-count faint num">
			{shown === total ? t("table.rows", { total }) : t("table.showing", { shown, total })}
		</p>
	);
}

/**
 * A row of bars with their numbers printed.
 *
 * The same rule as the trend on the overview: the value is labelled, because a
 * shape without a number is a picture rather than a measurement.
 */
function Bars({
	rows,
	percentage = false,
}: {
	rows: { label: string; value: number; text: string; warn?: boolean }[];
	percentage?: boolean;
}) {
	const max = Math.max(1, ...rows.map((row) => row.value));
	return (
		<div className="detail-bars">
			{rows.map((row) => (
				<div className="detail-bar" key={row.label}>
					<span className="detail-bar-label faint mono">{row.label}</span>
					<span className="bar">
						<span
							style={{
								width: `${(row.value / max) * 100}%`,
								background: row.warn
									? "var(--warning)"
									: magnitudeColor(percentage ? row.value : (row.value / max) * 100),
							}}
						/>
					</span>
					<span className="detail-bar-value num mono">{row.text}</span>
				</div>
			))}
		</div>
	);
}

// ---------------------------------------------------------------------------
// The affordance
// ---------------------------------------------------------------------------

/**
 * A number or a name that opens onto its rows.
 *
 * A button and not a link: it opens a panel over this screen rather than
 * navigating, and a control that looks like a link but does not change the
 * address is how a customer loses their place. The dotted underline is the
 * whole signal — a table of fifty underlined figures would read as fifty
 * links, so the mark only firms up under the pointer.
 */
const drillViews = new WeakMap<HTMLButtonElement, DetailView>();

/** Sorting and filtering belong to the table. Read its displayed row order,
 * keeping the same cell and drill position when a row exposes several metrics. */
function tableDrills(button: HTMLButtonElement): DetailView[] | undefined {
	const cell = button.closest("td");
	const table = cell?.closest("table");
	if (!cell || !table) return undefined;
	const position = Array.from(cell.querySelectorAll<HTMLButtonElement>("button.drill")).indexOf(button);
	return Array.from(table.rows).flatMap((row) => {
		const peer = row.cells[cell.cellIndex]?.querySelectorAll<HTMLButtonElement>("button.drill")[position];
		const view = peer && drillViews.get(peer);
		return view ? [view] : [];
	});
}

export function Drill({
	view,
	className,
	title,
	children,
}: {
	view: DetailView;
	className?: string;
	title?: string;
	children: ReactNode;
}) {
	const { open } = useDetail();
	return (
		<Button
			variant="link"
			className={`h-auto justify-start whitespace-normal p-0 text-left ${className ? `drill ${className}` : "drill"}`}
			type="button"
			title={title ?? view.label}
			ref={(button) => {
				if (button) drillViews.set(button, view);
			}}
			onClick={(event) => open(view, tableDrills(event.currentTarget))}
		>
			{children}
		</Button>
	);
}

/** The same affordance where a route, not a drawer, is the right answer. */
export function DrillLink({ to, children }: { to: string; children: ReactNode }) {
	return (
		<Link className="drill" to={to}>
			{children}
		</Link>
	);
}
